import * as validationLib from "../../lib/validation";
import ChevronDown from "../icons/ChevronDown";
import cn from "../../lib/classnames";
import GreenCheck from "../icons/GreenCheck";
import React, {useRef, useCallback, useState, useEffect} from "react";
import RedX from "../icons/RedX";
import styles from "./Select.module.css";
import textStyles from "../../styles/Text.module.css";

export const EMPTY_VALUE = "";

type Props = React.ComponentProps<"select"> & {
    className?: string;
    extraInfo?: string;
    id: string;
    label: string;
    noEmptyDefault?: boolean;
    options: string[];
    selectedIndex: number;
    setSelected: (index: number, option: string) => void;
    validationMessage?: string[];
};

function Select(
    {
        id,
        label,
        options,
        selectedIndex,
        setSelected,
        validationMessage,
        noEmptyDefault = false,
        extraInfo,
        onBlur,
        className,
        ...rest
    }: Props,
    ref: React.ForwardedRef<HTMLSelectElement>,
) {
    const [infoCollapsed, setInfoCollapsed] = useState(true);

    const containerRef = useRef<HTMLDivElement>(null);
    const borderBottomRef = useRef<HTMLDivElement>(null);

    const hasValidationMsg = !!validationMessage && !!validationMessage.length;

    const showValidIcon = useCallback(() => {
        return !noEmptyDefault && !hasValidationMsg && selectedIndex !== -1;
    }, [noEmptyDefault, selectedIndex, hasValidationMsg]);

    useEffect(() => {
        if (!borderBottomRef.current || !containerRef.current) {
            return;
        }

        if (hasValidationMsg || showValidIcon()) {
            const fullWidth = containerRef.current.getBoundingClientRect().width;
            const scale = (fullWidth - 40) / fullWidth;
            borderBottomRef.current.style.transform = `scaleX(${scale})`;
        } else {
            borderBottomRef.current.style.transform = "scaleX(1)";
        }
    }, [showValidIcon, hasValidationMsg]);

    return (
        <div ref={containerRef} className={cn(className, styles.container)}>
            <div className={styles.selectContainer}>
                <select
                    ref={ref}
                    id={id}
                    className={cn(
                        styles.select,
                        textStyles.input,
                        selectedIndex === -1 ? styles.noneSelected : undefined,
                        hasValidationMsg || showValidIcon() ? styles.selectWithIcon : undefined,
                    )}
                    value={selectedIndex}
                    onChange={(e) => {
                        const value = e.target.value;
                        if (value !== "") {
                            const index = parseInt(value);
                            setSelected(index, options[index]);
                        }
                    }}
                    onFocus={() => setInfoCollapsed(false)}
                    onBlur={(e) => {
                        setInfoCollapsed(true);
                        if (onBlur) {
                            onBlur(e);
                        }
                    }}
                    {...rest}
                >
                    {label ? (
                        <option value={EMPTY_VALUE} disabled={noEmptyDefault}>
                            {label}
                        </option>
                    ) : null}
                    {options.map((o, i) => (
                        <option key={o} value={i}>
                            {o}
                        </option>
                    ))}
                </select>
                <label
                    className={cn(styles.label, selectedIndex === -1 ? styles.empty : undefined)}
                    htmlFor={id}
                    data-content={label}
                >
                    <span className={styles.hidden}>{label}</span>
                </label>
                <ChevronDown
                    className={cn(
                        styles.chevron,
                        selectedIndex === -1 ? styles.empty : undefined,
                        hasValidationMsg ? styles.invalid : undefined,
                        noEmptyDefault ? styles.rightChevron : undefined,
                    )}
                />
                <RedX
                    className={cn(
                        styles.validityIcon,
                        hasValidationMsg ? styles.showInvalidIcon : undefined,
                    )}
                />
                <GreenCheck
                    className={cn(
                        styles.validityIcon,
                        showValidIcon() ? styles.showValidIcon : undefined,
                    )}
                />
                <div
                    ref={borderBottomRef}
                    className={cn(
                        styles.bottomBorder,
                        hasValidationMsg ? styles.invalid : undefined,
                    )}
                />
            </div>

            {validationMessage !== undefined && validationMessage.length ? (
                <div className={styles.validation}>
                    {validationLib.getDisplayString(validationMessage)}
                </div>
            ) : null}
            {extraInfo ? (
                <div
                    className={cn(
                        textStyles.inputDetail,
                        styles.extraInfo,
                        infoCollapsed ? styles.collapsed : styles.expanded,
                    )}
                >
                    {extraInfo}
                </div>
            ) : null}
        </div>
    );
}

export default React.forwardRef<HTMLSelectElement, Props>(Select);
